import React, { useState, useEffect } from 'react';
import { DndProvider, useDrag, useDragLayer, useDrop } from 'react-dnd';
import useFetchSafe from 'hooks/useFetchSafe';
import useModalForm from 'hooks/useModalForm';
import GetUrls from 'ts/enums/Url';
import { ButtonStyle, Layout, Product } from 'ts/types';
import useModalStore from 'store/modal';
import { getEmptyImage, HTML5Backend } from 'react-dnd-html5-backend';
import { CheckBox, Input } from 'components/ui';
import { ButtonStyleItem } from 'components';
import { SpinnerIcon } from 'components/svg';
import { useTrader } from 'context/TraderTypeContext';

const LayoutAddPage = () => {
  const Url = GetUrls();
  const { traderId } = useTrader();

  const params = useModalStore((state) => state.params) as Layout;
  const { ModalForm, form } = useModalForm();
  const { setValue, register } = form;
  const [isLoading, setLoading] = useState<boolean>(true);
  const [rowNo, setRowNo] = useState<number>(4);
  const [colNo, setColNo] = useState<number>(4);
  const [gridItems, setGridItems] = useState<Array<{ type: string; name: string; positionId: number, productId:number, buttonStyleId: number } | null>>(
    Array.from({ length: 12 }, (): null => null)
  );

  const { data: ProductData, isLoading: productLoading } = useFetchSafe<Product>(Url.getProduct(traderId));
  const { data: LayoutData, isLoading: layoutLoading} = useFetchSafe(`${Url.productLayout}${params ? params.layoutId : ''}`);
  const { data: ButtonStyleOpt, isLoading: buttonStyleLoading} = useFetchSafe<ButtonStyle>(Url.getButtonStyle);
  
  const activeProducts = ProductData?.filter(product => product.flActive == true);
  
  const getItemName = (productId: number): string | undefined => {
    const itemName = activeProducts.find(item => item.productId === productId);
    if (itemName) {
      return itemName.name;
    }
  };

  const getButtonStyleId = (productId: number): number | undefined => {
    const itemStyle = activeProducts.find(item => item.productId === productId);
    if (itemStyle) {
        return itemStyle.buttonStyleId;
    }
  };

  useEffect(() => {
    setValue('traderId', Number(traderId));
    if (params) {
      setValue('layoutId', params.layoutId);
      setRowNo(params.numberOfRows);
      setColNo(params.numberOfColumns);
    }  else {
      setValue('numberOfRows', 4);
      setValue('numberOfColumns', 4);
      console.log("firing")
    }
  }, [params, setValue])
  
  useEffect(() => {
    if (LayoutData && ProductData) {
      let newGrid: Array<{ type: string; name: string; index: number; productId: number; positionId: number; buttonStyleId:number }> = [];
        /* @ts-ignore */
      LayoutData.forEach((data: { productId: number; positionId: number, buttonStyleId:number }) => {
        const { productId, positionId, buttonStyleId } = data;
        const index = positionId - 1;
        newGrid[index] = {
          type: 'external_item',
          name: getItemName(productId) || "Default",
          index: index,
          productId: productId,
          positionId: positionId,
          buttonStyleId: getButtonStyleId(productId) || 1095,
        };
      });
      setGridItems(newGrid);
    }
  }, [isLoading]);
  
  useEffect(() => {
    if (gridItems) {
      setValue('layoutData', gridItems)
    }
  }, [gridItems])

  useEffect(() => {
    if (!productLoading && !layoutLoading && !buttonStyleLoading) {
      setLoading(false)
      console.log("buttonstyle",ButtonStyleOpt)
    }
  }, [productLoading, layoutLoading, buttonStyleLoading])

  const ItemTypes = {
    ITEM: 'item',
    EXTERNAL_ITEM: 'external_item',
  };

  const renderButton = (name: string, buttonStyleId: number) => {
    const data = ButtonStyleOpt.find(item => item.buttonStyleId === buttonStyleId);

    return (
      <ButtonStyleItem
        data={{
          buttonStyleId: buttonStyleId,
          name: name,
          foreground: data?.foreground || '',
          borderstyle: data?.borderstyle || '',
          text: data?.text || '',
          flActive: data?.flActive || true,
        }}
      />
    );
  };

  const CustomDragLayer = () => {
    const { isDragging, item, currentOffset, initialClientOffset, initialSourceClientOffset } = useDragLayer((monitor) => ({
      isDragging: monitor.isDragging(),
      item: monitor.getItem() as { name: string; buttonStyleId: number } | null,
      currentOffset: monitor.getClientOffset(),
      initialClientOffset: monitor.getInitialClientOffset(),
      initialSourceClientOffset: monitor.getInitialSourceClientOffset(),
    }));

    if (!isDragging || !item || !currentOffset) {
      return null;
    }

    const x = initialClientOffset && initialSourceClientOffset
      ? currentOffset.x - initialClientOffset.x + initialSourceClientOffset.x
      : currentOffset.x;
    const y = initialClientOffset && initialSourceClientOffset
      ? currentOffset.y - initialClientOffset.y + initialSourceClientOffset.y
      : currentOffset.y;

    return (
      <div className='pointer-events-none fixed left-0 top-0 z-100'>
        <div
          style={{
            opacity: 1,
            transform: `translate(${x}px, ${y}px)`,
            filter: 'drop-shadow(0 10px 18px rgba(0, 0, 0, 0.25))',
          }}
        >
          {renderButton(item.name, item.buttonStyleId)}
        </div>
      </div>
    );
  };

  const Item: React.FC<{ name: string; productId:number, index: number; fromGrid: boolean, 
    buttonStyleId: number }> = 
    ({ name, index, productId, fromGrid, buttonStyleId }) => {
  
    const [{ isDragging }, drag, preview] = useDrag(()=>({
      type: fromGrid ? ItemTypes.ITEM : ItemTypes.EXTERNAL_ITEM,
      item: { type: fromGrid ? ItemTypes.ITEM : ItemTypes.EXTERNAL_ITEM, name, index, productId, buttonStyleId },
      end: (_, monitor) => {
        if (!fromGrid || monitor.didDrop()) {
          return;
        }

        setGridItems((currentGridItems) => {
          const nextGridItems = [...currentGridItems];
          nextGridItems[index] = null;
          return nextGridItems;
        });
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),[buttonStyleId, fromGrid, index, name, productId])

    useEffect(() => {
      preview(getEmptyImage(), { captureDraggingState: true });
    }, [preview]);
  
    return (
      <div
        ref={drag}
        className='hover:opacity-80 h-fit-content'
        style={{ opacity: isDragging ? 0 : 1 }}
      >
        <div className='z-50 relative'>
          {renderButton(name, buttonStyleId)}
        </div>
      </div>
    );
  };

  const Grid: React.FC<{ index: number }> = ({ index }) => {
    const dropHandle = (droppedItem: any, gridIndex: number) => {
      const newGridItems = [...gridItems];
      if (droppedItem.type === ItemTypes.ITEM) {
        const existingItem = newGridItems[gridIndex];
        if (existingItem) {
          newGridItems[gridIndex] = { ...droppedItem, positionId: gridIndex + 1 };
          newGridItems[droppedItem.index] = { ...existingItem, positionId: droppedItem.index + 1 };
        } else {
          newGridItems[gridIndex] = { ...droppedItem, positionId: gridIndex + 1 };
          newGridItems[droppedItem.index] = null;
        }
        setGridItems(newGridItems);
      } else if (droppedItem.type === ItemTypes.EXTERNAL_ITEM) {
        newGridItems[gridIndex] = { ...droppedItem, positionId: gridIndex + 1 };
        setGridItems(newGridItems);
      }
    };

    const [{ isOver }, drop] = useDrop({
      accept: [ItemTypes.ITEM, ItemTypes.EXTERNAL_ITEM],
      drop: (item: { type: string; name: string; index: number, positionId: number, productId: number, buttonStyleId:number }, monitor) => {
        dropHandle(item, index)
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    });

    return (
      <div
        ref={drop}
        className={`h-16 w-24 border rounded-lg z-50 relative flex items-center justify-center bg-opacity-50 bg-gray-200 ${isOver ? "bg-indigo-400 border-black" : ""}`}
      >
        {gridItems[index]?.name &&
        <Item
          name={gridItems[index]!.name}
          index={index} fromGrid={true}
          productId={gridItems[index]!.productId}
          buttonStyleId={gridItems[index]!.buttonStyleId}
        />}
      </div>
    );
  };

  const LayoutEditor = () => {
    const [, layoutDrop] = useDrop({
      accept: [ItemTypes.ITEM, ItemTypes.EXTERNAL_ITEM],
      drop: (item: { type: string; index: number }, monitor) => {
        if (monitor.didDrop()) {
          return undefined;
        }

        if (item.type !== ItemTypes.ITEM) {
          return undefined;
        }

        setGridItems((currentGridItems) => {
          const nextGridItems = [...currentGridItems];
          nextGridItems[item.index] = null;
          return nextGridItems;
        });

        return { removed: true };
      },
    });

    return (
      <>
        <CustomDragLayer />
        <div ref={layoutDrop} className="flex gap-6">
          <div className="w-full">
            {params ? <Input defaultValue={params.layoutId} label="ID" disabled /> : null}
            <div className='flex'>
              <CheckBox defaultChecked={params?.flActive || false} className='w-24 z-20' id='active' label="Active" {...register('flActive')} />
              <Input 
                id='layoutname'
                className='z-20 w-full' 
                defaultValue={params?.name || ''} 
                label="Layout Name" 
                {...register('name', { required: true })}
              />
            </div>
            <div className='z-30 relative flex mt-2 gap-3 max-h-96 flex-wrap overflow-auto items-start'>
              {activeProducts.map((itemName, index) => (
                <Item key={index} name={itemName.name} index={index} productId={itemName.productId} fromGrid={false} buttonStyleId={itemName.buttonStyleId}/>
              ))}
            </div>
          </div>
          <div className="w-full">
            <div className="flex gap-6 w-full">
              <Input
                defaultValue={colNo}
                value={colNo}
                id='NoOfCol'
                type="number"
                label="Number of Columns"
                className='w-full z-20'
                step={1}
                onChange={handleColumnChange}
              />
              <Input
                defaultValue={colNo}
                value={rowNo}
                id='NoOfRow'
                type="number"
                label="Number of Rows"
                className='w-full z-20'
                step={1}
                onChange={handleRowChange}
              />
            </div>
            <div className='grid gap-2 relative z-50 mt-2' style={{ gridTemplateColumns: `repeat(${colNo}, 1fr)` }}>
              {Array.from({ length: colNo * rowNo }).map((_, index) => (
                <Grid key={index} index={index} />
              ))}
            </div>
          </div>
        </div>
      </>
    );
  };

  const handleColumnChange = (e:any) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value)) {
      setColNo(value);
      setValue('numberOfColumns', value);
    }
  };

  const handleRowChange = (e:any) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value)) {
      setRowNo(value);
      setValue('numberOfRows', value);
    }
  };

  return (
    <ModalForm
      config={{ url: Url.postLayout, method: params ? 'PUT' : 'POST' }}
      title={`${params ? 'Edit ' : 'Create '}New Layout`}
      description="Create or edit a layout"
    >
      { !isLoading ?     
      <DndProvider backend={HTML5Backend}>
        <LayoutEditor />
      </DndProvider>
      :
      <div className='flex items-center justify-center h-40'>
        <SpinnerIcon dark />
      </div>
      }
    </ModalForm>
  );
};

export default LayoutAddPage;
